import { useInjection } from 'inversify-react';
import { useCallback, useMemo } from 'react';

import { Button } from '@desygner/ui-common-components';
import { useTranslation } from '@desygner/ui-common-translation';
import {
  Design,
  TYPES as PROJECT_SERVICE_TYPES,
  ProjectsFacade,
  useProjectsStore,
} from '@desygner/ui-features-projects';
import { AccessSwitchMode, ShareAccess, ShareAccessLevel } from '@desygner/ui/common-share';

import { TYPES } from '../../../../../../../inversify.types';
import { IUserStoreInjector, IViewerStoreInjector } from '../../../../../../../store';
import { useResponsive } from '../../../../../../hooks/useResponsive';
import { useToggle } from '../../../../../../hooks/useToggle';

export const ShareWithAccessMenu = () => {
  const { t } = useTranslation();
  const { isTabletOrMobile } = useResponsive();
  const { state: isPopoverOpen, toggle: togglePopover } = useToggle(false);
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const useUserStore = useInjection<IUserStoreInjector>(TYPES.Store.User);
  const isWorkspace = useViewerStore((state) => state.company?.is_workspace);
  const projectType = useViewerStore((state) => state.project.type);
  const projectName = useProjectsStore((state) => state.project.currentProject?.name ?? '');
  const isOwner = useViewerStore((state) => state.project.isOwner ?? false);
  const isPublic = useProjectsStore((state) => state.project.currentProject?.is_public);
  const isTeamShared = useProjectsStore(
    (state) => (state.project.currentProject as Design)?.is_team_shared
  );
  const collaborators = useViewerStore((state) => state.collaborators);
  const userId = useUserStore((state) => state.userId);
  const hasCollaborationPermissions = useMemo(
    () =>
      collaborators?.some(
        (collaborator) =>
          collaborator.permissionUserId === userId && collaborator.permissionType !== 'owner'
      ),
    [collaborators, userId]
  );

  const ProjectService = useInjection<ProjectsFacade>(PROJECT_SERVICE_TYPES.ProjectsFacade);
  const isDesign = projectType === 'design';
  const shouldShowTeamSharedOption = isDesign && isWorkspace;

  const handleOnAccessChange = useCallback(
    async (access: ShareAccessLevel) => {
      const updatePayload: { is_public: boolean; is_team_shared?: boolean } = {
        is_public: isPublic || false,
      };

      switch (access) {
        case ShareAccessLevel.Private:
          updatePayload.is_public = false;
          updatePayload.is_team_shared = false;
          break;
        case ShareAccessLevel.TeamShared:
          updatePayload.is_public = false;
          updatePayload.is_team_shared = true;
          break;
        case ShareAccessLevel.Public:
          updatePayload.is_public = true;
          if (isDesign && isWorkspace) {
            updatePayload.is_team_shared = false;
          }

          break;
        default:
          access satisfies never;
          break;
      }

      await ProjectService.updateProject(updatePayload);
    },
    [ProjectService, isPublic, isWorkspace, isDesign]
  );

  const access = useMemo(() => {
    if (isPublic) {
      return ShareAccessLevel.Public;
    }

    if (!isPublic && shouldShowTeamSharedOption && isTeamShared) {
      return ShareAccessLevel.TeamShared;
    }

    return ShareAccessLevel.Private;
  }, [shouldShowTeamSharedOption, isPublic, isTeamShared]);

  const handleOnOpenChange = useCallback(
    (_open: boolean) => {
      togglePopover();
    },
    [togglePopover]
  );

  return (
    <ShareAccess
      shareLink={window.location.href}
      open={isPopoverOpen}
      accessSwitchMode={!isOwner ? AccessSwitchMode.Hidden : undefined}
      access={access}
      onOpenChange={handleOnOpenChange}
      disablePortal
      onAccessChange={handleOnAccessChange}
      features={{
        [ShareAccessLevel.Public]: isDesign,
        [ShareAccessLevel.TeamShared]: shouldShowTeamSharedOption,
      }}
      projectName={projectName}
      isTeamShared={hasCollaborationPermissions}
      isOwner={isOwner}
      projectType={projectType}
    >
      <Button
        action="share"
        title={isTabletOrMobile ? undefined : t('tool.topbar.share_tooltip')}
        variant="ghost"
        size="Small"
        icon={
          !isTabletOrMobile
            ? {
                name: 'share---triangle',
                size: 'Small',
              }
            : undefined
        }
        onClick={() => null}
        label={isTabletOrMobile ? t('tool.topbar.share_button') : undefined}
      />
    </ShareAccess>
  );
};
